import React, { useCallback } from "react";

import { faSliders } from "@fortawesome/pro-solid-svg-icons/faSliders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Form from "antd/es/form";
import { useForm } from "antd/es/form/Form";
import Popover from "antd/es/popover";
import { useIntl } from "react-intl";

import { Button } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import TimelineEventType, {
  getTimelineEventTypeDisplayName,
} from "@app/scene/dashboard/enum/TimelineEventType";

import styles from "./CustomTypeFilter.module.scss";

interface CustomTypeFilterProps {
  onEventTypesChange?: (eventTypes: TimelineEventType[]) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

interface Props extends CustomTypeFilterProps {
  eventTypes: TimelineEventType[];
}

const CustomTypeFilter: React.FC<Props> = ({
  eventTypes,
  onEventTypesChange,
  onOpenChange,
  open,
}) => {
  const intl = useIntl();

  const [form] = useForm<Record<TimelineEventType, boolean>>();

  const handleApply = useCallback(() => {
    const values = form.getFieldsValue();
    onEventTypesChange?.(Object.values(TimelineEventType).filter((type) => values[type]));
    onOpenChange?.(false);
  }, [onEventTypesChange, onOpenChange, form]);

  return (
    <Popover
      content={
        <Form
          className={styles.form}
          form={form}
          initialValues={Object.values(TimelineEventType).reduce((result, type) => {
            result[type] = eventTypes.includes(type);
            return result;
          }, {} as Record<TimelineEventType, boolean>)}
          onFinish={handleApply}
        >
          <div className={styles.grid}>
            {Object.values(TimelineEventType).map((type) => (
              <Form.Item key={type} name={type} valuePropName="checked">
                <Checkbox>{getTimelineEventTypeDisplayName(intl, type)}</Checkbox>
              </Form.Item>
            ))}
          </div>
          <div className={styles.footer}>
            <Button htmlType="submit" onClick={handleApply} type="primary">
              {intl.formatMessage({
                id: "dashboard.timeline.header.visibleActions.apply",
                defaultMessage: "Apply",
                description:
                  "Apply button in the Visible actions popover on the Timeline view on the Dashboard page",
              })}
            </Button>
          </div>
        </Form>
      }
      destroyTooltipOnHide
      onOpenChange={onOpenChange}
      open={open}
      placement="bottomRight"
      showArrow={false}
      title={intl.formatMessage({
        id: "dashboard.timeline.header.visibleActions",
        defaultMessage: "Visible Actions",
        description:
          "Title of the Visible actions popover on the Timeline view on the Dashboard page",
      })}
      trigger={["click"]}
    >
      <Button icon={<FontAwesomeIcon icon={faSliders} />} iconOnly />
    </Popover>
  );
};

export default CustomTypeFilter;
